import React, { useState } from 'react';
import { format } from 'date-fns';
import EditNoteModal from './EditNoteModal';
import { UserRoleLabels, NoteVisibilityRoleKeys } from '../utils/enums';

// Define LocationPreference enum
const LocationPreference = {
  KIAWAH: 'KIAWAH',
  ISLE_OF_PALMS: 'ISLE_OF_PALMS',
  ISLE_OF_PALMS_WILD_DUNES: 'ISLE_OF_PALMS_WILD_DUNES',
  SEABROOK_ISLAND: 'SEABROOK_ISLAND'
};

// Define grouped area combinations
const AREA_GROUPS = {
  KIAWAH_SEABROOK: {
    value: 'KIAWAH_SEABROOK',
    label: 'Kiawah & Seabrook',
    areas: [LocationPreference.KIAWAH, LocationPreference.SEABROOK_ISLAND],
    isGroup: true
  },
  IOP_WILD_DUNES: {
    value: 'IOP_WILD_DUNES', 
    label: 'Isle of Palms & Wild Dunes',
    areas: [LocationPreference.ISLE_OF_PALMS, LocationPreference.ISLE_OF_PALMS_WILD_DUNES],
    isGroup: true
  }
};

// Available areas array for display labels (keeping individual for backward compatibility)
const AREA_LABELS = {
  [LocationPreference.KIAWAH]: 'Kiawah',
  [LocationPreference.ISLE_OF_PALMS]: 'Isle of Palms',
  [LocationPreference.ISLE_OF_PALMS_WILD_DUNES]: 'Isle of Palms - Wild Dunes',
  [LocationPreference.SEABROOK_ISLAND]: 'Seabrook Island'
};

const NoteCard = ({ note, canManage, userRole, onDelete, deleteLoading }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const formattedDate = (date) => {
    if (!date) return null;
    // Handle date-only strings to avoid timezone conversion issues
    const dateStr = typeof date === 'string' ? date : new Date(date).toISOString();
    const dateOnly = dateStr.split('T')[0]; // Get YYYY-MM-DD part
    const [year, month, day] = dateOnly.split('-');
    // Create date in local timezone to avoid shifting
    return format(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)), 'MMM d, yyyy');
  };

  const formatCreatedAt = (date) => {
    if (!date) return '';
    return format(new Date(date), 'MMM d, yyyy h:mm a');
  };

  const formatAreas = (areas) => {
    if (!areas || areas.length === 0) return 'All Areas';
    
    // Check if the areas match any of our defined groups
    const kiawahSeabrookAreas = AREA_GROUPS.KIAWAH_SEABROOK.areas;
    const iopWildDunesAreas = AREA_GROUPS.IOP_WILD_DUNES.areas;
    
    // Check if all areas in the note match Kiawah & Seabrook group
    if (areas.length === kiawahSeabrookAreas.length && 
        kiawahSeabrookAreas.every(area => areas.includes(area))) {
      return AREA_GROUPS.KIAWAH_SEABROOK.label;
    }
    
    // Check if all areas in the note match Isle of Palms & Wild Dunes group
    if (areas.length === iopWildDunesAreas.length && 
        iopWildDunesAreas.every(area => areas.includes(area))) {
      return AREA_GROUPS.IOP_WILD_DUNES.label;
    }
    
    // Fallback to individual area names for backward compatibility
    return areas.map(area => AREA_LABELS[area] || area).join(', ');
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-md overflow-hidden transition-all duration-200 hover:shadow-lg hover:border-sky-100">
      <div className="p-5">
        <div className="flex justify-between items-start">
          <h3 className="text-xl font-semibold text-gray-900 mb-1">{note.title}</h3>
          
          {canManage && (
            <div className="flex space-x-1">
              <button
                onClick={() => setIsEditModalOpen(true)}
                className="text-gray-400 hover:text-sky-500 p-1 rounded-full hover:bg-sky-50 transition-colors duration-150"
                aria-label="Edit note"
                tabIndex="0"
                onKeyDown={(e) => e.key === 'Enter' && setIsEditModalOpen(true)}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={onDelete}
                disabled={deleteLoading}
                className="text-gray-400 hover:text-red-500 disabled:opacity-50 p-1 rounded-full hover:bg-red-50 transition-colors duration-150"
                aria-label="Delete note"
                tabIndex="0"
                onKeyDown={(e) => e.key === 'Enter' && onDelete()}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
        
        <div className="mt-3 note-content prose prose-sm max-w-none text-gray-700" 
             dangerouslySetInnerHTML={{ __html: note.body }} />
        
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex flex-wrap justify-between items-start text-xs mb-2">
            <div className="text-gray-600 font-medium">
              <span>Posted by <span className="text-sky-700">{note.author.name}</span> on {formatCreatedAt(note.createdAt)}</span>
            </div>
            
            {(note.startDate || note.endDate) && (
              <div className="mt-2 sm:mt-0 bg-sky-50 text-sky-800 px-3 py-1 rounded-full text-xs font-medium">
                {note.startDate && note.endDate && (
                  <span>Active: {formattedDate(note.startDate)} - {formattedDate(note.endDate)}</span>
                )}
                {note.startDate && !note.endDate && (
                  <span>Active from: {formattedDate(note.startDate)}</span>
                )}
                {!note.startDate && note.endDate && (
                  <span>Active until: {formattedDate(note.endDate)}</span>
                )}
              </div>
            )}
          </div>
          
          {/* Areas display */}
          <div className="flex items-center mt-2">
            <span className="text-xs text-gray-500 mr-2">Areas:</span>
            <div className="bg-green-50 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
              {formatAreas(note.visibleToAreas)}
            </div>
          </div>
        </div>
      </div>
      
      {/* Edit Note Modal */}
      {isEditModalOpen && (
        <EditNoteModal
          note={note}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            setIsEditModalOpen(false);
            // Note will be refetched automatically through GraphQL cache
            window.location.reload(); // Simple refresh for now
          }}
          availableRoles={NoteVisibilityRoleKeys}
          roleLabels={UserRoleLabels}
          areaGroups={AREA_GROUPS}
        />
      )}
    </div>
  );
};

export default NoteCard; 